/**
 * lib/tier.js — Lane 4 (Feb 2026)
 *
 * Single canonical source-of-truth helper for "is this user on a paid
 * plan?" — used by:
 *   - Dashboard.jsx home-branch (`<FreeHome />` for free users)
 *   - LeftRail.jsx locked-item styling and /pricing redirect
 *   - FreeLessonForm.jsx routing decision (free path vs full generator)
 *   - MyFreeLessons.jsx quota/badge visibility
 *   - Gradebook.jsx and any future page-level tier gate
 *
 * The signal is `user.subscription.is_active` as computed server-side by
 * `routes/auth.py:auth_me` (mirrors backend `lesson_quota.is_paid`
 * exactly — same ACTIVE_STATUSES set). Admin emails are also treated as
 * paid so founders never hit the free-tier walled garden.
 *
 * Do NOT reuse the LeftRail's `_hasFeature` tier-grants map for the
 * home-branch decision — that's a stale mirror of the backend tier
 * feature_flags kept for per-item lock decisions. This function is the
 * one truth for "is the caller a paying customer?".
 */

export const isPaidUser = (user) =>
  !!(user?.subscription?.is_active || user?.is_admin);

/** Convenience — narrower "signed in as a free-tier user". */
export const isSignedInFreeUser = (user) => !!user && !isPaidUser(user);

/**
 * Report-card-nudge lane (Feb 2026) — surgical Basic-tier match.
 *
 * The nudge banner ONLY renders for `tier_id === "basic"` on the paid
 * dashboard. Standard / Pro / Pro+ users have Report Card Builder and
 * must never see the nudge (would be advertising a feature they
 * already own). Admins are excluded even if they're on Basic (they
 * flip the switch — showing it back to them would be noisy).
 *
 * Strict equality on a whitelisted value is used deliberately —
 * negation logic (`!includes(...)`) would silently start firing for
 * new tiers introduced in the future.
 */
export const isBasicUser = (user) =>
  !!user
  && !user.is_admin
  && user?.subscription?.tier_id === "basic"
  && !!user?.subscription?.is_active;

/**
 * hasLiveReportComments(user) — Lane 1 (Tier Packaging).
 *
 * Mirrors the backend `LIVE_REPORT_COMMENTS` flag, which is granted to
 * Pro and Pro+ only (see `subscription_tiers.py`). This is a SEPARATE
 * capability from Report Card Builder (`REPORT_CARD_BUILDER`), which
 * Standard keeps. Admins pass through, matching `isPaidUser` above.
 *
 * When false, the frontend simply does not render the Live Report
 * Comments controls — no locked state, no upsell.
 */
export const hasLiveReportComments = (user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  const tier = (
    user?.subscription?.is_active ? user.subscription.tier_id || "" : ""
  ).toLowerCase();
  return tier === "pro" || tier === "pro_plus";
};
